export enum ImageMimeType {
   JPG = 'image/jpg',
   JPEG = 'image/jpeg',
   PNG = 'image/png',
   GIF = 'image/gif',
   BMP = 'image/bmp',
   WEBP = 'image/webp',
   SVG = 'image/svg+xml',
   TIFF = 'image/tiff',
   ICO = 'image/x-icon',
   HEIC = 'image/heic',
   HEIF = 'image/heif',
   AVIF = 'image/avif'
}

export enum VideoType {
   MP4 = 'video/mp4',
   WEBM ='video/webm'
}

export const getImageMimeTypes = (types: ImageMimeType[]): string => {
   return types?.join(', ') || Object.values(ImageMimeType).join(', ');
}
export const getVideoTypes = (types: VideoType[]): string => {
   return types?.join(', ') || Object.values(VideoType).join(', ');
}

/**
 * Resolve a supplier hotel image URL to a requested size.
 *
 * Supplier URLs arrive in two shapes and a given field can carry either, so both
 * have to be handled or one of them silently breaks:
 *
 *   1. `https://cdn.../{size}/photo.jpg`   — a literal `{size}` placeholder
 *   2. `https://cdn.../s=312x200/photo.jpg` — an already-sized `s=WxH` segment
 *
 * Substituting only the placeholder leaves shape 2 at whatever the supplier
 * defaulted to (the blurry-thumbnail bug, NPT-1584). Rewriting only `s=WxH`
 * leaves a literal `{size}` in the `src` for shape 1, which is a broken image.
 *
 * @param url          raw supplier URL, possibly undefined
 * @param placeholder  value substituted for `{size}` (supplier form, e.g. 'x500' or '640x400')
 * @param querySize    value for the `s=` segment (e.g. '640x400'); defaults to `placeholder`
 */
export const resolveImageSize = (url?: string | null, placeholder = 'x500', querySize?: string): string => {
   if (!url) return '';

   const size = querySize ?? placeholder;

   return url
      .split('{size}')
      .join(placeholder)
      // Global: a URL can carry the segment more than once (path and query).
      .replace(/s=\d+x\d*/gi, `s=${size}`)
      // Same segment, URL-encoded — seen on supplier URLs nested in a query string.
      .replace(/s%3D\d+x\d*/gi, `s%3D${size}`);
};

/**
 * Normalise an image URL for rendering.
 *
 * Returns `undefined` — never an empty string — whenever there is nothing safe
 * to render, so callers can branch on a single falsy check and omit the element
 * entirely. `<img src="">` re-requests the current page and paints the browser's
 * broken-image glyph, which is exactly the state this app should never show.
 *
 * Anything still carrying an unsubstituted `{...}` placeholder counts as
 * missing: those are supplier templates we have no size for, and requesting
 * them 404s.
 *
 * @param url        raw URL, possibly undefined
 * @param size       value substituted for `{size}` (see `resolveImageSize`)
 * @param querySize  value for the `s=` segment; defaults to `size`
 */
export const getImageSrc = (url?: string | null, size?: string, querySize?: string): string | undefined => {
   // Size the URL only when the caller asked for a size. Running the resizer
   // unconditionally would rewrite an already-sized `s=WxH` segment to the
   // resolver's own default and quietly downgrade callers that want the URL
   // as it came (the gallery's 1024x768, for one).
   const sized = size || querySize
      ? resolveImageSize(url, size, querySize)
      // A bare `{size}` template is still unrenderable, so substitute the
      // supplier default even when no size was requested.
      : (url || '').split('{size}').join('x500');

   const resolved = sized.trim();
   if (!resolved || /\{[^}]*\}/.test(resolved)) return undefined;
   return resolved;
};

/**
 * Drop entries whose image URL is unusable, for galleries and carousels.
 *
 * A carousel that maps over the raw list renders a blank slide and a navigation
 * dot for every missing URL. Filtering first means an empty result is an honest
 * "no images", which the caller can render as nothing at all.
 */
export const filterValidImages = <T,>(images: T[] | null | undefined, getUrl: (image: T) => string | null | undefined): T[] =>
   (images || []).filter(image => !!getImageSrc(getUrl(image)));
